import type { FunctionComponent, ReactNode, SVGProps } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { CheckCircleIcon } from '@gto/ui/icons/solid'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { BenefitIcon } from './benefit-icon'

export type PromoBenefitsCardProps = {
  title: ReactNode
  icon: FunctionComponent<SVGProps<SVGSVGElement>>
  description?: string[]
  prize: number
  topPrize: ReactNode
  link?: string
}

export const PromoBenefitsCard = ({
  title,
  description,
  icon: Icon,
  prize,
  topPrize,
  link,
}: PromoBenefitsCardProps) => {
  return (
    <Paper
      className="flex h-full shrink-0 grow-[1] basis-auto flex-col gap-4 rounded-md border border-divider px-6 py-4"
      elevation="1"
    >
      <div className="flex h-full flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <BenefitIcon>
              <Icon
                height={28}
                width={28}
              />
            </BenefitIcon>
            <Typography
              component="p"
              variant="h6"
            >
              {title}
            </Typography>
          </div>
          <div className="flex items-center gap-1">
            <Typography variant="subtitle2">
              <FormattedMessage
                id="promo.page.prizeSum"
                values={{
                  prize: (
                    <FormattedNumber
                      currency="USD"
                      minimumFractionDigits={0}
                      style="currency"
                      value={prize}
                    />
                  ),
                  highlight: (chunks) => (
                    <Typography
                      color="primary"
                      component="span"
                      variant="h6"
                    >
                      {chunks}
                    </Typography>
                  ),
                }}
              />
            </Typography>
          </div>
          <div className="flex flex-col">
            <Typography
              color="warning"
              variant="subtitle1"
            >
              <FormattedMessage id="promo.page.topPrize" />
            </Typography>
            <Typography
              color="text.primary"
              variant="subtitle3"
            >
              {topPrize}
            </Typography>
          </div>
        </div>
        <div className="flex-[1_0_auto]">
          {description && description.length > 0 && (
            <ul className="relative m-0 flex list-none flex-col gap-0.5 p-1">
              {description.map((item, index) => (
                <li
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- static benefit bullet list, never reorders
                  key={index}
                  className="relative flex w-full items-center justify-start p-0 text-left"
                >
                  <div className="mr-2 text-primary-main">
                    <CheckCircleIcon
                      height={16}
                      width={16}
                      style={{
                        verticalAlign: 'middle',
                      }}
                    />
                  </div>
                  <div className="my-1 min-w-0 flex-auto">
                    <Typography
                      color="text.secondary"
                      variant="body2"
                    >
                      {item}
                    </Typography>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          color="secondary"
          component={Link}
          href={link}
          size="large"
          variant="contained"
        >
          <FormattedMessage id="common.controls.moreInfo" />
        </Button>
      </div>
    </Paper>
  )
}
